/**
 * Wann war der letzte GESAMTSYNC — und ist das noch in Ordnung?
 *
 * Wunsch von Tobias (04.10.2026): unter dem Sync-Knopf „Allgemein" soll
 * stehen, wann zuletzt alle Stufen abgeglichen wurden, „so dass meine Frau
 * weiß, dass das System up to date ist — oder halt nicht".
 *
 * Gesamtsync = alle sechs Stufenseiten werden auf Etsy abgeklopft. Das ist
 * NICHT dasselbe wie „der Scraper arbeitet": Stufenwechsel, Notizen und
 * Nachrichten laufen laufend über kleine Syncs (`queues_only`), die keine
 * Übersichtsseite laden. Der Gesamtsync ist der Abgleich in die andere
 * Richtung — er findet, was AUSSERHALB unseres Systems passiert ist: eine auf
 * Etsy von Hand verschobene Stufe, eine dort abgeschlossene Bestellung, eine
 * Bestellung, die es nur auf Etsy gibt.
 *
 * Deshalb ist „veraltet" hier kein Alarm, dass etwas kaputt ist. Es heißt:
 * seit X wurde nicht mehr gegengeprüft.
 *
 * Diese Datei ist von der Oberfläche getrennt, damit die Regel testbar ist —
 * `OrdersPage.jsx` zieht `react-router-dom` nach und lässt sich in Jest nicht
 * laden. Dieselbe Trennung wie bei `reviewRules.js`.
 */

const MS_PRO_STUNDE = 3600 * 1000;

// Ab wann die Anzeige die Farbe wechselt. Bewusst großzügig: ein Gesamtsync
// wird heute ausschließlich von Hand ausgelöst (keine Cloud Function fordert
// einen an — `pollEtsyNewOrders` schreibt `neu_only`), und er ist mit 8–10
// Seitenaufrufen am Stück der größte Etsy-Kontakt, den wir haben. Ein Tag
// ohne ist völlig normal.
export const SCHWELLE_FRISCH_STUNDEN = 24;
export const SCHWELLE_ALT_STUNDEN = 72;

/** Alter in Stunden, oder null wenn kein Zeitpunkt bekannt ist. */
export function alterInStunden(datum, jetzt) {
  if (!(datum instanceof Date) || isNaN(datum.getTime())) return null;
  return (jetzt.getTime() - datum.getTime()) / MS_PRO_STUNDE;
}

/**
 * „frisch" | „alt" | „veraltet" | „unbekannt"
 *
 * Ein negatives Alter gilt als frisch: die beiden PCs und der Firestore-Server
 * laufen nicht sekundengleich, und „vor -2 Minuten" wäre eine sinnlose
 * Meldung für eine Uhrendifferenz.
 */
export function stufeFuerAlter(stunden) {
  if (stunden === null || !isFinite(stunden)) return "unbekannt";
  if (stunden < SCHWELLE_FRISCH_STUNDEN) return "frisch";
  if (stunden < SCHWELLE_ALT_STUNDEN) return "alt";
  return "veraltet";
}

/** „vor 12 Min." / „vor 3 Std." / „vor 2 Tagen" */
export function formatAlter(stunden) {
  if (stunden === null || !isFinite(stunden)) return "";
  if (stunden < 0) return "gerade eben";
  const minuten = Math.round(stunden * 60);
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Min.`;
  const std = Math.floor(stunden);
  if (std < 48) return `vor ${std} Std.`;
  const tage = Math.floor(stunden / 24);
  return `vor ${tage} Tagen`;
}

function uhrzeit(datum) {
  return `${String(datum.getHours()).padStart(2, "0")}:${String(
    datum.getMinutes()
  ).padStart(2, "0")}`;
}

function kalendertageAuseinander(datum, jetzt) {
  const a = new Date(datum.getFullYear(), datum.getMonth(), datum.getDate());
  const b = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  return Math.round((b - a) / (24 * MS_PRO_STUNDE));
}

/**
 * „heute 06:12" / „gestern 19:02" / „29.09. um 22:10"
 *
 * Nach Kalendertagen, nicht nach Stundenabstand: um 00:30 ist ein Lauf von
 * 23:50 „gestern", auch wenn er nur 40 Minuten her ist. Genau so liest ein
 * Mensch das.
 */
export function formatZeitpunkt(datum, jetzt) {
  if (!(datum instanceof Date) || isNaN(datum.getTime())) return "";
  const tage = kalendertageAuseinander(datum, jetzt);
  if (tage === 0) return `heute ${uhrzeit(datum)}`;
  if (tage === 1) return `gestern ${uhrzeit(datum)}`;
  const tag = String(datum.getDate()).padStart(2, "0");
  const monat = String(datum.getMonth() + 1).padStart(2, "0");
  return `${tag}.${monat}. um ${uhrzeit(datum)}`;
}

/**
 * Alles, was die Anzeige braucht.
 *
 * `datum` ist der letzte Gesamtsync (oder null/undefined, wenn keiner erfasst
 * ist). Die Farbe entscheidet die Oberfläche anhand von `stufe` — hier stehen
 * keine Farbwerte, damit der Test die Regel prüft und nicht einen Hexwert.
 */
export function beschreibeGesamtsync(datum, jetzt = new Date()) {
  const stunden = alterInStunden(datum, jetzt);
  const stufe = stufeFuerAlter(stunden);
  if (stufe === "unbekannt") {
    return {
      stufe,
      stunden: null,
      zeitpunkt: "",
      alter: "",
      text: "Gesamtsync: noch nicht erfasst",
    };
  }
  const zeitpunkt = formatZeitpunkt(datum, jetzt);
  const alter = formatAlter(stunden);
  return {
    stufe,
    stunden,
    zeitpunkt,
    alter,
    text: `Gesamtsync: ${zeitpunkt} · ${alter}`,
  };
}

/**
 * Welcher Zeitpunkt gilt?
 *
 * Vorrang hat `lastFullSyncAt` — das schreibt der Scraper nur nach einem
 * Gesamtsync. Solange der Scraper noch die Fassung von vor dem 04.10.2026
 * läuft (er lädt seinen Code beim Start, ein Neustart ist nötig), schreibt er
 * das Feld nicht. Für dieses Fenster zählt ersatzweise `lastCompletedAt` —
 * aber nur, wenn der zuletzt gefahrene Modus überhaupt ein Gesamtsync war.
 *
 * Die Modusprüfung ist ein Ausschluss, genau wie im Scraper: jeder unbekannte
 * Modus läuft dort als voller Scrape durch, auch das historische „full".
 */
export const TEILSYNC_MODI = ["queues_only", "neu_only", "shipping", "reviews"];

export function istGesamtsyncModus(modus) {
  return !TEILSYNC_MODI.includes(modus || "scrape");
}

/**
 * Was der letzte Gesamtsync bewegt hat — in zwei Gruppen, und das ist der
 * ganze Punkt.
 *
 * Tobias, 04.10.2026: „dann merkt ggfls. meine Frau, dass die Syncläufe kaum
 * Mehrwert bringen." Dafür müssen die Zahlen die richtige Frage beantworten,
 * und die lautet nicht „wie viel ist passiert", sondern „was wäre OHNE diesen
 * Lauf nicht passiert".
 *
 * GEFUNDEN — nur ein Gesamtsync findet das:
 *   · eine Stufe, die auf Etsy von Hand verschoben wurde
 *   · eine Bestellung, die es nur auf Etsy gibt
 *   · eine Bestellung, die dort abgeschlossen wurde (archiviert)
 *
 * NEBENBEI — erledigt er mit, aber die kleinen automatischen Läufe tun das
 * sowieso, alle paar Minuten:
 *   · Stufenwechsel, die WIR ausgelöst haben, auf Etsy setzen
 *   · private Notizen schreiben
 *
 * Etsy-Nachrichten stehen bewusst in KEINER der beiden Gruppen: sie sind kein
 * Teil eines Syncs. Der Scraper arbeitet diese Warteschlange bei jedem Poll
 * ab, alle paar Sekunden, unabhängig davon, ob ein Sync läuft. Eine Zahl „pro
 * Gesamtsync" wäre dort keine Messung, sondern eine Erfindung.
 *
 * `null` heißt „nicht erfasst" und wird als solches angezeigt — nicht als 0.
 * Eine 0 würde behaupten, der Lauf habe nichts gefunden; das wäre bei den
 * nachgetragenen Werten vom 04.10.2026 eine Erfindung.
 */
export function gesamtsyncBilanz(daten) {
  const d = daten || {};
  const z = (w) => (typeof w === "number" && isFinite(w) ? w : null);

  const funde = {
    stufenVonEtsy: z(d.lastFullSyncStagesFromEtsy),
    neu: z(d.lastFullSyncCount),
    archiviert: z(d.lastFullSyncArchived),
  };
  const arbeit = {
    stufenGesetzt: z(d.lastFullSyncStagesSet),
    notizen: z(d.lastFullSyncNotes),
  };

  const werte = (o) => Object.values(o).filter((v) => v !== null);
  const fundWerte = werte(funde);
  const arbeitWerte = werte(arbeit);

  return {
    funde,
    arbeit,
    // „erfasst" nur, wenn mindestens eine Zahl da ist.
    fundeErfasst: fundWerte.length > 0,
    arbeitErfasst: arbeitWerte.length > 0,
    // Die Kernaussage: hat der Lauf etwas gefunden, das sonst niemand findet?
    nichtsGefunden: fundWerte.length > 0 && fundWerte.every((v) => v === 0),
  };
}

const EINHEITEN = {
  stufenVonEtsy: ["Stufe von Etsy", "Stufen von Etsy"],
  neu: ["neue Bestellung", "neue Bestellungen"],
  archiviert: ["archiviert", "archiviert"],
  stufenGesetzt: ["Stufenwechsel", "Stufenwechsel"],
  notizen: ["Notiz", "Notizen"],
};

/** „2 Stufen von Etsy · 1 neue Bestellung" — Nullen werden weggelassen. */
export function formatBilanzGruppe(gruppe) {
  const teile = Object.entries(gruppe)
    .filter(([, v]) => typeof v === "number" && v > 0)
    .map(([k, v]) => {
      const [eins, viele] = EINHEITEN[k] || [k, k];
      return `${v} ${v === 1 ? eins : viele}`;
    });
  return teile.join(" · ");
}

export function gesamtsyncZeitpunkt(daten) {
  const d = daten || {};
  const alsDatum = (w) =>
    w && typeof w.toMillis === "function"
      ? new Date(w.toMillis())
      : w instanceof Date
      ? w
      : null;

  const ausFeld = alsDatum(d.lastFullSyncAt);
  const ersatz = istGesamtsyncModus(d.mode) ? alsDatum(d.lastCompletedAt) : null;

  if (ausFeld && ersatz) return ausFeld > ersatz ? ausFeld : ersatz;
  return ausFeld || ersatz || null;
}
